import { Component, signal, computed, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../Services/api-service';
import { ApiResponse } from '../../models/api-response';
import { ToasterService } from '../../Services/toaster-service';
import { LocalizationService } from '../../Services/localization-service';
import { environment } from '../../../environments/environment';

interface CartItem {
  id: string;
  name: string;
  description: string;
  basePriceMonthly: number;
  basePriceYearly: number;
  quantity: number;
  maxQuantity: number;
  highlighted?: boolean;
}

interface AppliedCoupon {
  id: string;
  code: string;
  percentageOff: number; // 0-100
  isValid: boolean;
}

interface CheckOutData {
  productId: string;
  orderId: string;
  discountCodeId: string;
  paymentIntervalUnit: number;
}
@Component({
  selector: 'app-cart-component',
  imports: [
    CommonModule,
    TranslatePipe,
    RouterModule
  ],
  templateUrl: './cart-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './cart-component.scss'
})
export class CartComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  // Signals for reactive state
  productId = signal<string | null>(null);
  billingCycle = signal<'monthly' | 'yearly'>('monthly');
  items = signal<CartItem[]>([]);
  currentLang = environment.defaultLang;
  couponInput = signal<string>('');
  appliedCoupon = signal<AppliedCoupon | null>(null);
  checkOutData: CheckOutData = {} as CheckOutData;
  applyingCoupon = signal<boolean>(false);
  paymentPolicyAccepted = signal<boolean>(false);

  constructor() {
    const id = this.route.snapshot.paramMap.get('productId');
    if (id) {
      this.productId.set(id);
      this.createOrder(id);
    }
    this.localizationService.getLanguage().subscribe(lang => {
      if (lang != '') {
        this.currentLang = lang;
        this.cdr.detectChanges();
      }

    });
  }

  createOrder(productId: string) {
    this.apiService.postData(`api/v1/order/createOrder/${productId}`, {})
      .subscribe({
        next: (response: ApiResponse) => {
          let item = response.data as CartItem;
          item.maxQuantity = 1;
          this.items.set([item]);
          this.checkOutData.productId = productId;
          this.checkOutData.orderId = item.id;
          this.checkOutData.paymentIntervalUnit = this.billingCycle() === 'monthly' ? 3 : 4;
          this.cdr.detectChanges();
        }
      });
  }

  // Derived pricing per item based on billing cycle
  itemTotals = computed(() => {
    const cycle = this.billingCycle();
    return this.items().map(item => {
      const base = cycle === 'monthly' ? item.basePriceMonthly : item.basePriceYearly; // yearly show total year price
      return base * item.quantity;
    });
  });

  subTotal = computed(() => this.itemTotals().reduce((a, b) => a + b, 0));

  discountAmount = computed(() => {
    const coupon = this.appliedCoupon();
    if (!coupon) return 0;
    return (this.subTotal() * coupon.percentageOff) / 100;
  });

  total = computed(() => this.subTotal() - this.discountAmount());

  cycleLabel = computed(() => this.billingCycle() === 'monthly' ? 'Monthly' : 'Yearly');

  toggleCycle(cycle: 'monthly' | 'yearly') {
    if (this.billingCycle() !== cycle) {
      this.billingCycle.set(cycle);
      this.checkOutData.paymentIntervalUnit = cycle === 'monthly' ? 3 : 4;
    }
  }

  increment(item: CartItem) {
    this.items.update(list => list.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i));
  }

  decrement(item: CartItem) {
    this.items.update(list => list.map(i => i.id === item.id && i.quantity > 1 ? { ...i, quantity: i.quantity - 1 } : i));
  }

  applyCoupon() {
    const code = this.couponInput().trim().toUpperCase();
    if (!code) {
      this.toaster.showInfo(this.translate.instant('Enter a coupon code.'));
      return;
    }
    this.applyingCoupon.set(true);
    this.apiService.postData(`api/v1/order/ValidateDiscountCode/${code}`, {})
      .subscribe({
        next: (response: ApiResponse) => {
          let validateResult = response.data as AppliedCoupon;
          if (validateResult.isValid) {
            this.appliedCoupon.set(validateResult);
            this.checkOutData.discountCodeId = validateResult.id;
          } else {
            this.appliedCoupon.set(null);
            this.toaster.showDanger(this.translate.instant('Invalid coupon'));
          }
          this.applyingCoupon.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.applyingCoupon.set(false);
          this.appliedCoupon.set(null);
          this.toaster.showDanger(this.translate.instant('Failed to validate coupon. Please try again.'));
          this.cdr.detectChanges();
        }
      });
  }

  removeCoupon() {
    this.appliedCoupon.set(null);
    this.couponInput.set('');
    this.checkOutData.discountCodeId = '';
  }
  setPaymentPolicyAccepted(accepted: boolean) {
    this.paymentPolicyAccepted.set(accepted);
  }

  checkout() {
    this.apiService.postData(`api/v1/order/Checkout/`, this.checkOutData)
      .subscribe({
        next: (response: ApiResponse) => {
          if (response.isSuccess) {
            const checkoutUrl = response.data.checkoutUrl;
            window.location.href = checkoutUrl;
          } else {
            const msg = Array.isArray(response.messages) && response.messages.length
              ? response.messages.join(' ')
              : this.translate.instant('Checkout failed. Please try again later.');
            this.toaster.showDanger(msg);
          }
        },
        error: (err) => {
          const apiErr = err as { error?: ApiResponse; message?: string };
          const msg = apiErr?.error?.messages?.length ? apiErr.error.messages.join(' ') : (apiErr.message || 'Server error during checkout.');
          this.toaster.showDanger(this.translate.instant(msg));
          this.cdr.detectChanges();
        }
      });
  }
}
